import { useRoutes } from "react-router-dom";
import { Suspense, useEffect } from "react";
import TuiGrid from 'tui-grid'
import { locale, addLocale, updateLocaleOptions } from 'primereact/api';
import { ToastContainer } from "react-toastify";

import "portal/assets/css/lib/toast/tui-grid.css";
import "portal/assets/css/lib/toast/tui-pagination.css";
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'react-quill/dist/quill.snow.css';
import "portal/assets/css/style.css";
import "portal/assets/css/loading.css";
import "portal/assets/css/sub.css";
import 'portal/assets/css/lib/ozreport/ui.dynatree.css';
import "./App.css";

import NotFound from "app/pages/error/NotFound";
import { indexRouter } from "app/routers/indexRouter";
import {useAppDispatch, useAppSelector} from "app/store/hook";
import { publicRouter } from "app/routers/publicRouter";
import { HolidayService } from "app/services/common/holidayService";
import { ResponseResultCode } from "app/model/response-api";
import { updateHolidays } from "app/reducers/holidaySlice";
import { CoService } from "app/services/common/CoService";
import { updateBzknd, updateBzkndOnUsers } from "app/reducers/bzkndSlice";
import { getWeather } from "app/reducers/weatherSlice";
import {getMgResources} from "app/reducers/mgResourceSlice";
import { getCommonCode } from "app/reducers/CodeSlice";
import { useAuth } from "react-oidc-context";

export const spinner = (
  <div className="progress-spinner text-center">
    <div className="lds-spinner">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  </div>
);

addLocale('ko', {
  firstDayOfWeek: 0,
  'dayNames': ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
  'dayNamesShort': ['일', '월', '화', '수', '목', '금', '토'],
  'dayNamesMin': ['일', '월', '화', '수', '목', '금', '토'],
  'monthNames': ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
  'monthNamesShort': ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
  'today': '오늘',
  'clear': '초기화'
});
updateLocaleOptions({ showMonthAfterYear: true }, 'ko');
locale('ko');
TuiGrid.setLanguage('ko');

function App() {
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const userInfo = useAppSelector(state => state.userInfo.userInfo);
  const holidays = useAppSelector((state) => state.holidays.holidays);
  const bzknd = useAppSelector(state => state.bzknd.bzknd);
  const bzkndOnUsers = useAppSelector(state => state.bzknd.bzkndOnUsers);
  const todayWeather = useAppSelector(state => state.weather.today);
  const mgResources = useAppSelector(state => state.mgResource.mgResources);
  const coCodes = useAppSelector(state => state.code.code);

  let router = useRoutes([
    ...publicRouter,
    indexRouter,
    { path: "*", element: <NotFound /> },
  ]);

  useEffect(() => {
    /*
    * 초반 앱 접근 시
    * 인증 받지 않은 사용자가 호출 할 경우 401이 발생하며 오류 toast message가 출력됨.
    * 인증 받은 사용자만 호출 할 수 있도록 auth를 추가함.
    * 추가로 현재 인증 받은 사용자와 스토리지에 저장된 사용자의 정보가 동일할때만 호출하도록 함.
    */
    if (userInfo && auth.isAuthenticated && auth.user?.profile.sub === userInfo.userId) {
      if (holidays == null || holidays.length === 0) {
        HolidayService.getInstance().getHolidays().then(response => {
          if (response?.data && ResponseResultCode.Y === response.resultCode) {
            dispatch(updateHolidays(response.data));
          }
        });
      }

      if (bzknd == null || bzknd.length === 0) {
        CoService.getInstance().getBzkndList().then(response => {
          if (response && ResponseResultCode.Y === response.resultCode) {
            dispatch(updateBzknd(response.data));
          }
        });
      }

      if (bzkndOnUsers == null || bzkndOnUsers.length === 0) {
        CoService.getInstance().getBzkndListOnUsers().then(response => {
          if (response && ResponseResultCode.Y === response.resultCode) {
            dispatch(updateBzkndOnUsers(response.data));
          }
        });
      }

      if (todayWeather == null || todayWeather.length === 0) {
        dispatch(getWeather());
      }

      if (mgResources == null || mgResources.length === 0) {
        dispatch(getMgResources());
      }

      if (coCodes == null || Object.keys(coCodes).length === 0) {
        dispatch(getCommonCode());
      }
    }
  }, [auth, userInfo]);

  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        pauseOnFocusLoss={false}
        hideProgressBar={false}
        closeOnClick
        limit={3}
      />
      <Suspense fallback={spinner}>{router}</Suspense>
    </div>
  );
}

export default App;
